import React, { useEffect, useState } from 'react';
import { Box, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { confirmResetPassword, confirmSignIn, resetPassword, ResetPasswordInput, signIn } from 'aws-amplify/auth';
import {
    appType,
    ButtonComponent,
    CognitoCallbacks,
    CognitoErrorCodes,
    OtpInput,
    colors,
} from '@watter/shared-components';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useTranslation } from 'react-i18next';
import { INavigationConfig, NavigationConfig } from '../../utils/navigation-constants';
import { AppDispatch } from '../../store';
import { LoginUi } from './login-style';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { VerifyIcon } from '../../components/icons/verify-icon';
import { openToast } from '../../store/reducers/toastSlice';
import { useHandleLoginOnSuccess } from './login-success';

export const VerifyLoginOtp: React.FC = () => {
    const timerValueInSec = 3 * 60;
    const navConfig: INavigationConfig = NavigationConfig;
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [otpValue, setOtpValue] = useState<string>('');
    const [timer, setTimer] = useState<number>(timerValueInSec);
    const [isResendEnabled, setIsResendEnabled] = useState<boolean>(false);
    const [isValidOtp, setIsValidOtp] = useState<boolean>(true);
    const location = useLocation();
    const type = location.state?.type;
    const username = location.state?.username;
    const password = location.state?.password;
    const isCalledFrom = location.state?.isCalledFrom;
    const { handleLoginOnSuccess } = useHandleLoginOnSuccess();

    const { t } = useTranslation();
    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | undefined;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer(prevTimer => prevTimer - 1);
            }, 1000);
        } else {
            setIsResendEnabled(true);
            if (interval) clearInterval(interval);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [timer]);

    const handleOtpChange = (otp: string, length: number) => {
        setOtpValue(otp);
        if (otp?.length === length) {
            setIsValidOtp(true);
        }
    };
    const resetOtpFromPassword = async () => {
        setIsValidOtp(false);
        setTimer(timerValueInSec);
        setIsResendEnabled(false);
        try {
            dispatch(showLoader());
            const payload: ResetPasswordInput = {
                username,
                options: {
                    clientMetadata: {
                        application_type: `${appType.Customer}`,
                    },
                },
            };
            const response = await resetPassword(payload);
            const resetPasswordStep = response?.nextStep?.resetPasswordStep;
            if (resetPasswordStep === CognitoCallbacks.CONFIRM_RESET_PASSWORD_WITH_CODE) {
                dispatch(
                    openToast({
                        show: true,
                        message: t('otp_sent_to_registered_email_message'),
                        severity: 'success',
                        isAlertOpen: true,
                    }),
                );
            }
        } catch (err: any) {
            dispatch(hideLoader());
            if (err?.name === CognitoErrorCodes.EMPTY_SIGNIN_USER_NAME) {
                dispatch(
                    openToast({
                        show: true,
                        message: t('empty_sign_in_exception_message'),
                        severity: 'error',
                        isAlertOpen: true,
                    }),
                );
                navigate(navConfig.Login);
            } else {
                dispatch(
                    openToast({
                        show: true,
                        message: err?.name,
                        severity: 'error',
                        isAlertOpen: true,
                    }),
                );
            }
        } finally {
            dispatch(hideLoader());
        }
    };
    const resendOtp = async () => {
        setIsValidOtp(false);
        setTimer(timerValueInSec);
        setIsResendEnabled(false);
        try {
            dispatch(showLoader());
            const user = await signIn({
                username,
                password,
                options: {
                    clientMetadata: {
                        application_type: `${appType.Customer}`,
                    },
                },
            });
            switch (user?.nextStep?.signInStep) {
                case CognitoCallbacks.CONFIRM_SIGN_IN_WITH_SMS_CODE:
                    dispatch(
                        openToast({
                            show: true,
                            message: t('otp_sent_to_registered_mobile_number'),
                            severity: 'success',
                            isAlertOpen: true,
                        }),
                    );
                    break;
                case CognitoCallbacks.CONFIRM_SIGN_IN_WITH_EMAIL_CODE:
                    dispatch(
                        openToast({
                            show: true,
                            message: t('otp_sent_to_registered_email_message'),
                            severity: 'success',
                            isAlertOpen: true,
                        }),
                    );
                    break;
                default:
            }
        } catch (err: any) {
            console.error(err, 'err');
            dispatch(hideLoader());
            if (err?.name === CognitoErrorCodes.EMPTY_SIGNIN_USER_NAME) {
                dispatch(
                    openToast({
                        show: true,
                        message: t('empty_sign_in_exception_message'),
                        severity: 'error',
                        isAlertOpen: true,
                    }),
                );
                navigate(navConfig.Login);
            } else {
                dispatch(
                    openToast({
                        show: true,
                        message: err?.name,
                        severity: 'error',
                        isAlertOpen: true,
                    }),
                );
            }
        } finally {
            dispatch(hideLoader());
        }
    };

    const verifyOtp = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const res = await confirmSignIn({ challengeResponse: otpValue?.toString() });
            if (res.nextStep.signInStep === CognitoCallbacks.DONE) {
                await handleLoginOnSuccess();
            }
        } catch (err: any) {
            console.error(err, '==> err');

            dispatch(hideLoader());
            switch (err?.name) {
                case CognitoErrorCodes.CODE_MISMATCH_EXCEPTION:
                    dispatch(
                        openToast({
                            show: true,
                            message: t('otp_invalid_message'),
                            severity: 'error',
                            isAlertOpen: true,
                        }),
                    );
                    break;
                case CognitoErrorCodes.NOT_AUTHORIZED_EXCEPTION:
                    dispatch(
                        openToast({
                            show: true,
                            message: t('otp_expired_message'),
                            severity: 'error',
                            isAlertOpen: true,
                        }),
                    );
                    break;
                case CognitoErrorCodes.SIGN_IN_EXCEPTION:
                    dispatch(
                        openToast({
                            show: true,
                            message: t('sign_in_exception_message'),
                            severity: 'error',
                            isAlertOpen: true,
                        }),
                    );
                    break;
                default:
                    dispatch(openToast({ show: true, message: err?.name, severity: 'error', isAlertOpen: true }));
            }
        } finally {
            dispatch(hideLoader());
        }
    }, [otpValue, t, dispatch, handleLoginOnSuccess]);

    const navigateBack = () => {
        switch (type) {
            case CognitoCallbacks.CONFIRM_SIGN_IN_WITH_EMAIL_CODE:
            case CognitoCallbacks.CONFIRM_SIGN_IN_WITH_SMS_CODE:
                navigate(navConfig.Login);
                break;
            default:
                navigate(-1);
        }
    };
    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const verifyConfirmOtp = React.useCallback(async () => {
        try {
            dispatch(showLoader());

            await confirmResetPassword({
                username,
                confirmationCode: otpValue.toString(),
                newPassword: password,
                options: {
                    clientMetadata: {
                        application_type: `${appType.Customer}`,
                    },
                },
            });

            // Success toast
            dispatch(
                openToast({
                    show: true,
                    message: t('password_reset_success_message'),
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );
            navigate(navConfig.Login); // Navigate on success
        } catch (err: any) {
            let message;
            dispatch(hideLoader());
            // Determine the error message based on the error name
            switch (err?.name) {
                case CognitoErrorCodes.CODE_MISMATCH_EXCEPTION:
                case CognitoErrorCodes.NOT_AUTHORIZED_EXCEPTION:
                    message = t('otp_invalid_message');
                    break;
                case CognitoErrorCodes.SIGN_IN_EXCEPTION:
                    message = t('sign_in_exception_message');
                    break;
                case CognitoErrorCodes.OTP_EXPIRED_EXCEPTION:
                    message = t('otp_expired_message');
                    break;
                default:
                    message = err?.name || t('unexpected_error_message');
            }

            // Dispatch the error toast with the resolved message
            dispatch(
                openToast({
                    show: true,
                    message,
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    }, [otpValue, t, dispatch, navigate, navConfig, password, username]);
    useEffect(() => {
        if (isValidOtp && otpValue.length === 6) {
            console.warn(otpValue, 'otp in handleOtpChange');
            if (type === CognitoCallbacks.CONFIRM_RESET_PASSWORD_WITH_CODE) {
                verifyConfirmOtp();
            } else {
                verifyOtp();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otpValue, type, isValidOtp]);

    return (
        <LoginUi container spacing={{ sm: 0, lg: 2 }}>
            <Grid2
                className="imgContainer"
                size={{ sm: 12, lg: 8 }}
                display={{ xs: 'none', lg: 'flex' }}
                alignItems="center"
            >
                <Box />
            </Grid2>
            <Grid2
                size={{ sm: 12, lg: 4 }}
                className="feildsLabels"
                position="relative"
                minWidth={{ xs: '100%', lg: 'auto' }}
            >
                <IconButton className="backBtn" onClick={navigateBack}>
                    <KeyboardBackspaceIcon />
                </IconButton>
                <Box height="100%">
                    <Box
                        pt={3}
                        px={5}
                        pb={2}
                        height="100%"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                    >
                        <Box textAlign="center" className="logo otpLogo">
                            <VerifyIcon />
                        </Box>
                        <OtpInput
                            isValidOtp={isValidOtp}
                            showText
                            type="password"
                            autoFocus
                            length={6}
                            onChange={handleOtpChange}
                        />
                        <Typography variant="h4" pt={1.5} textAlign="center">
                            {t('otp_verification_title')}
                        </Typography>
                        <Typography variant="h6" py={1.5} textAlign="center" color="info">
                            {isCalledFrom === NavigationConfig.Login
                                ? t('enter_otp_received_message')
                                : t('enter_otp_received_from_email_message')}
                        </Typography>
                        <Stack direction="row" alignItems="center" justifyContent="center" pb={1.5}>
                            <Typography variant="subtitle1" py={1.5} textAlign="center" color={colors.gray}>
                                {t('otp_not_recieved_message')}
                            </Typography>
                            {isCalledFrom === NavigationConfig.Login && (
                                <ButtonComponent
                                    className="cstResendBtn"
                                    variant="text"
                                    size="medium"
                                    type="submit"
                                    buttonName={t('otp_resend_button_title')}
                                    disabled={!isResendEnabled}
                                    onClick={isResendEnabled ? resendOtp : undefined}
                                />
                            )}
                            {isCalledFrom === NavigationConfig.ForgotPassword && (
                                <ButtonComponent
                                    className="cstResendBtn"
                                    variant="text"
                                    size="medium"
                                    type="submit"
                                    buttonName={t('otp_resend_button_title')}
                                    disabled={!isResendEnabled}
                                    onClick={isResendEnabled ? resetOtpFromPassword : undefined}
                                />
                            )}
                            <Typography variant="h6" className="timer">
                                {timer ? formatTime(timer) : ''}
                            </Typography>
                        </Stack>
                    </Box>
                </Box>
            </Grid2>
        </LoginUi>
    );
};
